import { useMemo, useState } from 'react';
import { InfoCircleFilled } from '@ant-design/icons';
import type { BasePaginationProps } from '@components';
import { BaseCheckbox, BasePagination, HtmlInjection } from '@components';
import NoDataServerError from '@components/empty-state';
import CloseIcon from '@components/modal/close-icon';
import { useAppDispatch, useAppSelector } from '@hooks/reduxHooks.ts';
import { useResponsive } from '@hooks/useResponsive.ts';
import { useLingui } from '@lingui/react/macro';
import { DescText, ItemText } from '@routes/___shared___/styled.tsx';
import { getFullUserInfo } from '@routes/___shared___/user.slice.ts';
import { getUserPreferencesOrDefaults } from '@routes/___shared___/utils.ts';
import { ChangesTitleWrapper } from '@routes/profile-bar/___pages___/my-profile/ui/submitted-changes-list-tab/styled.ts';
import { TabContentContainer, TabContentWrapper } from '@routes/profile-bar/___shared___/styled.tsx';
import { requestUserDeletion } from '@routes/sidebar/settings/model/settings.slice.ts';
import { formatDateBasedOnPreferences, formatDateTimeBasedOnPreferences as formatDate } from '@utils/date.ts';
import { isEmpty, orderBy } from 'lodash';

import RequestDataDeletionModal from './consent-modal';
import {
    BaseAlertBannerDesc,
    BaseAlertBannerWrapper,
    ConsentCenterDescDateWrapper,
    ConsentCenterDescWrapper,
    ConsentCenterItemRow,
    ConsentCenterListContainer,
} from './styled.ts';

const ConsentCenterList = () => {
    const { t } = useLingui();
    const { isBigScreen } = useResponsive();
    const dispatch = useAppDispatch();
    const userFullInfo = useAppSelector((state) => state.user.userInfo);
    const userPreferencesSettings = getUserPreferencesOrDefaults(userFullInfo);
    const { preferredDateFormat: dateFormat, preferredTimeFormat: timeFormat } = userPreferencesSettings;
    const consentsList = userFullInfo?.consents;
    const dataDeletionRequestDate = formatDateBasedOnPreferences(
        userFullInfo?.userState?.deletionRequestedAtUTC || '',
        dateFormat,
    );
    const dataDeletionRequestId = userFullInfo?.userState?.deletionRequestId || '';

    const [current, setCurrent] = useState<number>(1);
    const [pageSize, setPageSize] = useState<number>(10);
    const [isModalOpened, setIsModalOpened] = useState(false);
    const [isSubmitOk, setIsSubmitOk] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const INFO_DESCRIPTION = t`These consent settings are read-only and reflect your current agreements.`;

    const userConsentCenterList = useMemo(() => {
        const sorted = orderBy(consentsList, 'approvedDateUtc', 'desc');
        const startIndex = (current - 1) * pageSize;

        return sorted.slice(startIndex, startIndex + pageSize).map((content) => (
            <div key={content.id}>
                <ConsentCenterItemRow>
                    <ItemText>
                        {!!(content.title && content.description) && (
                            <DescText>
                                <HtmlInjection text={`<b>${content.title}</b> ${content.description}`} />
                            </DescText>
                        )}
                    </ItemText>
                    <BaseCheckbox checked={content.isApproved} disabled={Boolean(content.approvedDateUtc)} />
                </ConsentCenterItemRow>
                <ConsentCenterItemRow>
                    <ConsentCenterDescDateWrapper>
                        {t`Last change`}: {formatDate(content.approvedDateUtc || '', dateFormat, timeFormat)}
                    </ConsentCenterDescDateWrapper>
                </ConsentCenterItemRow>
            </div>
        ));
    }, [consentsList, current, pageSize, t, dateFormat, timeFormat]);

    const handlePageChange: BasePaginationProps['onChange'] = (page, size) => {
        setCurrent(page);
        setPageSize(size);
    };

    const handleDelete = () => {
        setIsLoading(true);
        dispatch(requestUserDeletion())
            .then(() => {
                setIsSubmitOk(true);
                setIsLoading(false);
                void dispatch(getFullUserInfo());
            })
            .catch(() => {})
            .finally(() => {
                setIsLoading(false);
            });
    };

    const consentsCount = (consentsList && consentsList.length) || 0;

    const hidePagination = consentsCount < pageSize;

    if (!consentsList) return <NoDataServerError />;

    // BBL-2110
    /*
    const isDataDeletionRequestSubmitted = !!dataDeletionRequestDate;
    const INFO_DESCRIPTION_SUBMITTED = t`You have requested your data to be deleted on ${dataDeletionRequestDate}. Request ID: ${dataDeletionRequestId}`;
    const TOOLTIP_CONTENT = t`This process is irreversible - once deletion is approved, access to your account and saved information will be permanently lost.`;
    const TOOLTIP_TITLE = <TooltipTitleWrapper>{TOOLTIP_CONTENT}</TooltipTitleWrapper>;
    const openModal = () => {
        setIsSubmitOk(false);
        setIsModalOpened(true);
    }; */

    return (
        <TabContentWrapper $hasData={!isEmpty(consentsList)}>
            {!isEmpty(consentsList) && (
                <>
                    <TabContentContainer $isBigScreen={isBigScreen} $hidePagination={hidePagination}>
                        <ChangesTitleWrapper>{t`Consent Center`}</ChangesTitleWrapper>
                        <ConsentCenterDescWrapper>{t`Manage your data and consent preferences`}</ConsentCenterDescWrapper>
                        <BaseAlertBannerWrapper
                            description={<BaseAlertBannerDesc>{INFO_DESCRIPTION}</BaseAlertBannerDesc>}
                            type="info"
                            icon={<InfoCircleFilled />}
                        />
                        <ConsentCenterListContainer>{userConsentCenterList}</ConsentCenterListContainer>
                    </TabContentContainer>
                    {!hidePagination && (
                        <BasePagination
                            align="end"
                            current={current}
                            pageSize={pageSize}
                            onChange={handlePageChange}
                            total={consentsCount}
                        />
                    )}
                    {/* BBL-2110 />*/}
                    {/*<BaseDividerWrapper />*/}
                    {/*<DeleteSection>*/}
                    {/*    <BaseRow align="middle">*/}
                    {/*        <UserDataDeleteTitle>{t`User Data Deletion`}</UserDataDeleteTitle>*/}
                    {/*        <BaseTooltip*/}
                    {/*            title={TOOLTIP_TITLE}*/}
                    {/*            placement="right"*/}
                    {/*            color={BORDER_COLORS.white}*/}
                    {/*            arrow={false}*/}
                    {/*            icon={<InfoIcon />}*/}
                    {/*        />*/}
                    {/*    </BaseRow>*/}
                    {/*    <UserDataDeleteDesc*/}
                    {/*        $isDataDeletionRequestSubmitted={isDataDeletionRequestSubmitted}*/}
                    {/*    >{t`Send request to delete your data`}</UserDataDeleteDesc>*/}
                    {/*    {isDataDeletionRequestSubmitted && (*/}
                    {/*        <ListContainer isBigScreen={isBigScreen}>*/}
                    {/*            <BaseAlertBannerWrapper*/}
                    {/*                description={*/}
                    {/*                    <BaseAlertBannerDesc>{INFO_DESCRIPTION_SUBMITTED}</BaseAlertBannerDesc>*/}
                    {/*                }*/}
                    {/*                type="warning"*/}
                    {/*                icon={<WarningFilled />}*/}
                    {/*            />*/}
                    {/*        </ListContainer>*/}
                    {/*    )}*/}
                    {/*    <BaseButton*/}
                    {/*        data-cy="btn-request-data-deletion"*/}
                    {/*        $customType="outline"*/}
                    {/*        iconPlacement="start"*/}
                    {/*        icon={<TrashIcon />}*/}
                    {/*        onClick={openModal}*/}
                    {/*        disabled={isDataDeletionRequestSubmitted}*/}
                    {/*    >*/}
                    {/*        <DeleteButtonTitle*/}
                    {/*            $isDataDeletionRequestSubmitted={isDataDeletionRequestSubmitted}*/}
                    {/*        >{t`Request Data Deletion`}</DeleteButtonTitle>*/}
                    {/*    </BaseButton>*/}
                    {/*</DeleteSection>*/}
                    <RequestDataDeletionModal
                        setIsOpen={setIsModalOpened}
                        closeIcon={<CloseIcon onClick={() => setIsModalOpened(false)} />}
                        open={isModalOpened}
                        onConfirmDelete={handleDelete}
                        isSubmitOk={isSubmitOk}
                        isLoading={isLoading}
                        submitDate={dataDeletionRequestDate}
                        requestId={dataDeletionRequestId}
                    />
                </>
            )}
        </TabContentWrapper>
    );
};
export default ConsentCenterList;
